// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledOverlay from "@conklin/App/Components/Common/StyledOverlay";
import XngUtilities from "@conklin/App/Components/Common/XngUtilities";
import XngZone from "@conklin/App/Components/Common/XngZone";
import useSize from "@conklin/App/Hooks/useSize";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import { getLogoImage, xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import { getMainNavigationWidget } from "@conklin/App/Store/Selectors/WidgetSelector";
import { checkoutPages, HEADER_HEIGHT_CSS_VAR } from "@conklin/XDL/constants";
import IconHeaderBranchLocator from "@conklin/XDL/Icons/IconHeaderBranchLocator";
import { createLinkIconStyles } from "@conklin/XDL/Utilities";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XNG-Changes
const mapStateToProps = (state: CustomAppState) => {
    const session = getSession(state);
    const currentLocation = getLocation(state);
    const changeCustomerPageUrl = getPageLinkByPageType(state, "ChangeCustomerPage")?.url;

    return {
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        // XNG-Changes
        isTabletScreen: state.xCommon.isTabletScreen,
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        currentLocation,
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
        mainNavigation: getMainNavigationWidget(state),
        isAuthenticated: xIsAuthenticated(state),
        locationsPageUrl: getPageLinkByPageType(state, "LocationFinderPage")?.url || "/LocationFinder",
        logoImage: getLogoImage(state),
        mobileSearchOpen: state.xHeader.mobileSearchOpen,
        displayChangeCustomerLink: session.displayChangeCustomerLink,
        homePageLink: getHomePageUrl(state),
        isInChangeCustomerPage: changeCustomerPageUrl === currentLocation.pathname,
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
    // XNG-Changes
    setNavDrawerIsOpen,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    // XNG-Changes
    topSection?: InjectableCss;
    topRightSection?: InjectableCss;
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
    headerMessage?: TypographyPresentationProps;
    printLogo?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, false, false, false],
    },
    container: {
        css: css`
            background-color: ${getColor("secondary.main")};
            display: flex;
            > div:first-child {
                flex-grow: 1;
            }
            > div:last-child {
                max-width: 150px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 100px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    // XNG-Changes
    fields,
    isTabletScreen,
    navDrawerIsOpen,
    currentLocation,
    isCheckoutPage,
    setNavDrawerIsOpen,
    mainNavigation,
    isAuthenticated,
    locationsPageUrl,
    logoImage,
    mobileSearchOpen,
    displayChangeCustomerLink,
    homePageLink,
    isInChangeCustomerPage,
}: Props) => {
    // <XNG-Changes>
    const headerRef = useRef<HTMLDivElement>(null);
    const headerHeight = useSize(headerRef)?.height;

    useEffect(() => {
        if (headerHeight) {
            if (typeof document === "undefined") {
                return;
            }
            const cssRoot = document.querySelector(":root") as HTMLElement;
            cssRoot.style.setProperty(HEADER_HEIGHT_CSS_VAR, `${headerHeight + 1}px`);
        }
    }, [headerHeight]);

    useEffect(() => {
        if (navDrawerIsOpen) {
            setNavDrawerIsOpen({ navDrawerIsOpen: false });
        }
    }, [currentLocation]);

    useEffect(() => {
        if (!displayChangeCustomerLink && homePageLink && isInChangeCustomerPage) {
            history.push(homePageLink);
        }
    }, [displayChangeCustomerLink, homePageLink, isInChangeCustomerPage]);
    // <XNG-Changes>

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {/* XNG-Changes: add logo for print view */}
            {logoImage && (
                <StyledWrapper {...styles.printLogo}>
                    <img alt={translate("Home")} src={logoImage} />
                </StyledWrapper>
            )}
            <Page as="header" isHeader={true} {...styles.page}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    {!isCheckoutPage ? (
                        <StyledWrapper ref={headerRef}>
                            <XngZone hint="Global Notification" contentId={id} zoneName="GlobalNotification" />
                            <StyledWrapper {...styles.container}>
                                <StyledWrapper {...styles.leftSection} {...{ isHidden: mobileSearchOpen }}>
                                    {isTabletScreen &&
                                        createWidgetElement("Header/MainNavigation", {
                                            fields: { ...mainNavigation },
                                        })}
                                    <Zone contentId={id} zoneName="Logo" />
                                </StyledWrapper>
                                <StyledWrapper {...styles.rightSection}>
                                    {isTabletScreen ? (
                                        <Zone contentId={id} zoneName="Header" />
                                    ) : (
                                        <>
                                            <StyledWrapper {...styles.topSection}>
                                                <Typography {...styles.headerMessage}>
                                                    {fields.headerMessage}
                                                </Typography>

                                                {isAuthenticated ? (
                                                    <StyledWrapper {...styles.topRightSection}>
                                                        <Zone contentId={id} zoneName="TopHeader" />
                                                    </StyledWrapper>
                                                ) : (
                                                    <Link
                                                        href={locationsPageUrl}
                                                        {...createLinkIconStyles(IconHeaderBranchLocator, "small")}
                                                    >
                                                        {translate("Branch Locator")}
                                                    </Link>
                                                )}
                                            </StyledWrapper>
                                            <Zone contentId={id} zoneName="Header" />
                                            <Zone contentId={id} zoneName="MainNavigation" />
                                        </>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    ) : (
                        <StyledWrapper ref={headerRef}>
                            <StyledWrapper {...styles.container} {...{ isCheckoutPage }}>
                                <StyledWrapper {...styles.leftSection} {...{ isCheckoutPage }}>
                                    <Zone contentId={id} zoneName="Logo" />
                                </StyledWrapper>
                                <StyledWrapper {...styles.rightSection}></StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
            <XngUtilities />
            <StyledOverlay />
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
        // XNG-Changes
        fieldDefinitions: [
            {
                name: "headerMessage",
                displayName: "Header Message",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Sheet Metal, Duct Fab & HVAC supplies—Founded in 1874",
            },
        ],
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
